import { styled } from "goober";
import { type ReactNode, useMemo, useRef, useState } from "react";
import { queries } from "../../../shared/queries";
import { useQuery } from "../../hooks/useQuery";
import { useScrollFade } from "../../hooks/useScrollFade";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../../hooks/useSpaceDisplayName";
import { formatRelativeTime } from "../../lib/formatRelativeTime";
import { makeUrl, routes } from "../../lib/urls";
import { Button } from "../Button";
import { GhostPill } from "../GhostPill";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { PressButton } from "../PressButton";
import { SearchField } from "../Settings/shared";
import { MetaSep } from "../MetaSeparator";
import { Text } from "../Text";
import { WorkflowBadge } from "../WorkflowBadge";
import {
  computeSpaceExecutionVolumeRows,
  computeWorkflowExecutionVolumeRows,
  executionVolumeBarFraction,
  formatExecutionVolume,
  workflowRowsForSpace,
} from "./executionVolume";
import {
  DETAIL_MODAL_STYLE,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderRight,
  ModalTitle,
} from "./MonitoringDetailCard";
import {
  Bar,
  BarRow,
  barColumnStyles,
  ColumnMeta,
  Columns,
} from "./monitoringBars";
import { ListSection, NameBody, NameCell } from "./monitoringList";
import { InfoCorner, PanelBadge } from "./PanelInfo";
import {
  MONITORING_CARD_MAX,
  MONITORING_PANEL_PADDING,
  MONITORING_SHORT_VIEWPORT_HEIGHT,
  spaceColorMap,
} from "./monitoringPanelLayout";
import { useMonitorSpaceWorkflows } from "./useMonitorSpaceWorkflows";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

// Interactive column (opens the per-space modal); the visuals are shared with
// Spend-by-model via barColumnStyles / the other primitives in monitoringBars.
// Columns grow to fill the panel but never shrink below a legible min width, so
// the row scrolls horizontally (see ScrollColumns) once there are too many
// spaces to fit. In the single-row layout the bars keep that fixed width too, so
// the card hugs/grows with the number of spaces up to a cap and then scrolls,
// rather than compressing the bars (and colliding their labels) to fit.
const Column = styled(PressButton)`
  ${barColumnStyles}
  flex: 1 0 64px;

  @media (min-width: 901px) and (max-height: ${MONITORING_SHORT_VIEWPORT_HEIGHT}px) {
    flex: 0 0 64px;
    max-width: 64px;
  }
`;

// Non-interactive column for the ghost/loading state.
const GhostColumn = styled("div")`
  ${barColumnStyles}
`;

// The bars' scroll container: grows to fit the panel, but once the columns hit
// their min width it scrolls horizontally with soft faded edges (driven by
// useScrollFade's --fade-left/--fade-right) instead of cramming every space in.
const ScrollColumns = styled(Columns)`
  overflow-x: auto;
  overflow-y: hidden;
  justify-content: flex-start;
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    black var(--fade-left, 0px),
    black calc(100% - var(--fade-right, 0px)),
    transparent
  );
  mask-image: linear-gradient(
    to right,
    transparent,
    black var(--fade-left, 0px),
    black calc(100% - var(--fade-right, 0px)),
    transparent
  );

  /* Single-row (short) layout: hug the bars up to the card's inner width, then
     scroll. Cap at the card's content box (max − both paddings) so the track sits
     inside the padding and its fade renders on the card, not under the edge. */
  @media (min-width: 901px) and (max-height: ${MONITORING_SHORT_VIEWPORT_HEIGHT}px) {
    width: fit-content;
    max-width: calc(
      ${MONITORING_CARD_MAX}px - 2 * ${MONITORING_PANEL_PADDING}px
    );
  }
`;

// Wraps the scroll container so useScrollFade mounts alongside it: this panel
// early-returns a skeleton while loading, so a hook on the parent would first
// run with a null ref and never re-fire once the real columns appear.
function FadeScrollColumns({
  children,
  ariaHidden,
}: {
  children: ReactNode;
  ariaHidden?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollFade({ scrollRef });
  return (
    <ScrollColumns ref={scrollRef} aria-hidden={ariaHidden || undefined}>
      {children}
    </ScrollColumns>
  );
}

const GHOST_COLUMNS = [
  { key: "ghost-1", count: 1200, labelWidth: 48, countWidth: 26 },
  { key: "ghost-2", count: 640, labelWidth: 40, countWidth: 22 },
  { key: "ghost-3", count: 210, labelWidth: 56, countWidth: 20 },
  { key: "ghost-4", count: 80, labelWidth: 36, countWidth: 18 },
  { key: "ghost-5", count: 12, labelWidth: 44, countWidth: 16 },
];

type SpaceRow = {
  space: DisplayableSpace;
  label: string;
  executionCount: number;
  workflowCount: number;
};

export function ExecutionVolumeBySpace() {
  const { workflows: statsWorkflows, result: statsQuery } =
    useMonitorSpaceWorkflows();
  const [spaces, spacesQuery] = useQuery(
    queries.spacesLite({ includeAdminSpaces: true })
  );
  const displayName = useSpaceDisplayName();
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { chartRows, totalExecutions, workflowRows } = useMemo(() => {
    const teamSpaces = (spaces ?? []).filter(
      (space) => space.mode !== "personal"
    );
    const teamSpaceIds = teamSpaces.map((space) => space.id);
    const workflowRows = computeWorkflowExecutionVolumeRows(statsWorkflows);
    const volumeBySpace = computeSpaceExecutionVolumeRows(
      workflowRows,
      teamSpaceIds
    );

    const chartRows: SpaceRow[] = volumeBySpace.map((row) => {
      const space = teamSpaces.find((entry) => entry.id === row.spaceId)!;
      return {
        space,
        label: displayName(space),
        executionCount: row.executionCount,
        workflowCount: row.workflowCount,
      };
    });

    const totalExecutions = chartRows.reduce(
      (sum, row) => sum + row.executionCount,
      0
    );

    return {
      chartRows,
      totalExecutions,
      workflowRows,
    };
  }, [statsWorkflows, spaces, displayName]);

  const selectedRow = chartRows.find((row) => row.space.id === selectedSpaceId);
  const selectedWorkflowRows = useMemo(() => {
    if (!selectedRow) return [];
    return workflowRowsForSpace(workflowRows, selectedRow.space.id);
  }, [selectedRow, workflowRows]);
  const filteredWorkflowRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return selectedWorkflowRows;
    return selectedWorkflowRows.filter((row) =>
      row.name.toLowerCase().includes(q)
    );
  }, [selectedWorkflowRows, search]);

  const colorMap = useMemo(() => spaceColorMap(spaces ?? []), [spaces]);
  const maxCount = Math.max(1, ...chartRows.map((row) => row.executionCount));

  if (statsQuery.type !== "complete" || spacesQuery.type !== "complete") {
    return <PanelSkeleton />;
  }

  return (
    <Panel>
      <InfoCorner>
        <PanelBadge>Last 30 days</PanelBadge>
      </InfoCorner>
      <PanelHeader>
        <Text variant="text-small-md">Execution volume by space</Text>
        <TotalRow>
          <Text variant="display-md">
            {formatExecutionVolume(totalExecutions)}
          </Text>
        </TotalRow>
      </PanelHeader>

      <FadeScrollColumns ariaHidden={chartRows.length === 0}>
        {chartRows.length === 0
          ? GHOST_COLUMNS.map((row) => (
              <GhostColumn key={row.key}>
                <BarRow>
                  <Bar
                    style={{
                      width: 6,
                      background: "var(--alpha8)",
                      minHeight: 6,
                      height: `${executionVolumeBarFraction(row.count, GHOST_COLUMNS[0]!.count) * 100}%`,
                    }}
                  />
                </BarRow>
                <ColumnMeta>
                  <GhostPill style={{ width: row.labelWidth }} />
                  <GhostPill style={{ width: row.countWidth }} />
                </ColumnMeta>
              </GhostColumn>
            ))
          : chartRows.map((row) => (
              <Column
                key={row.space.id}
                onMouseEnter={() => setHoveredId(row.space.id)}
                onMouseLeave={() => setHoveredId(null)}
                onPress={() => {
                  setSelectedSpaceId(row.space.id);
                  setSearch("");
                  setOpen(true);
                }}
                style={{
                  opacity:
                    hoveredId != null && hoveredId !== row.space.id ? 0.4 : 1,
                }}
              >
                <BarRow>
                  <Bar
                    data-bar
                    style={{
                      width: hoveredId === row.space.id ? 12 : 6,
                      background: colorMap.get(row.space.id)!,
                      minHeight: row.executionCount > 0 ? 6 : 0,
                      height: `${executionVolumeBarFraction(row.executionCount, maxCount) * 100}%`,
                    }}
                  />
                </BarRow>
                <ColumnMeta>
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {row.label}
                  </Text>
                  <Text
                    variant="text-small-md"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {formatExecutionVolume(row.executionCount)}
                  </Text>
                </ColumnMeta>
              </Column>
            ))}
      </FadeScrollColumns>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        style={DETAIL_MODAL_STYLE}
      >
        <ModalHeader>
          <ModalTitle
            icon="play"
            title="Execution volume"
            space={selectedRow?.space}
          />
          <ModalHeaderRight>
            <Button
              size="l"
              variant="secondary"
              icon="x"
              onClick={() => setOpen(false)}
            />
          </ModalHeaderRight>
        </ModalHeader>

        <ModalBody style={{ gap: 0 }}>
          <ListSection
            title="Busiest workflows"
            description="Ranked by executions on the live branch over the last 30 days."
            action={
              <SearchField
                placeholder="Search workflows…"
                value={search}
                onChange={setSearch}
                width={280}
                autoFocus
              />
            }
          >
            <ListTable
              headers={{
                name: "Workflow",
                executions: "Executions",
                lastRun: "Last run",
              }}
              columns={{ name: "flex", executions: "md", lastRun: "md" }}
              emptyState={
                <Text variant="text-regular" color="var(--text-secondary)">
                  {search
                    ? "No workflows match your search."
                    : "No live-branch runs in this space in the last 30 days."}
                </Text>
              }
              items={filteredWorkflowRows.map((row) => ({
                key: row.workflowId,
                link: selectedRow
                  ? makeUrl(routes.space.workflow, {
                      spaceId: selectedRow.space.id,
                      workflowId: row.workflowId,
                    })
                  : undefined,
                name: (hover: boolean) => (
                  <NameCell>
                    <WorkflowBadge
                      workflow={{
                        id: row.workflowId,
                        badgeSeed: row.badgeSeed,
                        pausedAt: row.pausedAt,
                        branches: [{ name: "main" }],
                      }}
                      hover={hover}
                    />
                    <NameBody>
                      <Text variant="text-regular-md" ellipsis>
                        {row.name}
                      </Text>
                    </NameBody>
                  </NameCell>
                ),
                executions: (
                  <Text variant="text-small">
                    {formatExecutionVolume(row.executionCount)}
                  </Text>
                ),
                lastRun: (
                  <Text variant="text-small">
                    {row.lastRunAt != null
                      ? formatRelativeTime(row.lastRunAt)
                      : "—"}
                  </Text>
                ),
              }))}
            />
          </ListSection>
        </ModalBody>

        <ModalFooter>
          <Text variant="text-small" color="var(--text-secondary)">
            {selectedRow && (
              <>
                {formatExecutionVolume(selectedRow.executionCount)} executions
                <MetaSep />
                {selectedRow.workflowCount}{" "}
                {selectedRow.workflowCount === 1 ? "workflow" : "workflows"}
              </>
            )}
          </Text>
          <Button size="l" variant="primary" onClick={() => setOpen(false)}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </Panel>
  );
}
